import { useState, FormEvent } from 'react';
import { Button } from '@mehir/web-ui/button';
import { Input } from '@mehir/web-ui/input';
import { Card, CardContent } from '@mehir/web-ui/card';
import { Alert, AlertDescription } from '@mehir/web-ui/alert';
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@mehir/web-ui/field';
import { useLoginAsyncMutation } from '@mehir/api/backoffice-api';
import { useAppDispatch } from '@app/hooks/redux';
import { loginStart, loginFailure, setToken } from '@app/store/auth-slice';
import { tokenStore } from '@app/store/api-config';
import { toast } from '@mehir/web-ui/hooks/use-toast';

export const LoginScreen = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const dispatch = useAppDispatch();
  const [loginAsync, { isLoading }] = useLoginAsyncMutation();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const validationErrors: string[] = [];
    if (!username.trim()) validationErrors.push('Kullanıcı adı gereklidir');
    if (!password.trim()) validationErrors.push('Şifre gereklidir');

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    dispatch(loginStart());

    try {
      const loginResult = await loginAsync({
        loginRequest: { username: username.trim(), password },
      }).unwrap();

      const accessToken = loginResult.accessToken;
      if (!accessToken) {
        throw new Error('Login response carried no access token');
      }

      tokenStore.save({ accessToken, refreshToken: loginResult.refreshToken });

      // Set token in Redux — useAuth hook will auto-fetch current user via useGetCurrentUserAsyncQuery
      dispatch(setToken(accessToken));

      toast({
        title: 'Giriş Başarılı',
        description: 'Hoş geldiniz!',
      });
    } catch (error: unknown) {
      dispatch(loginFailure());
      tokenStore.clear();

      const err = error as { status?: number };
      if (err?.status === 401 || err?.status === 400) {
        setErrors(['Geçersiz kullanıcı adı veya şifre']);
        toast({
          variant: 'destructive',
          title: 'Giriş Hatası',
          description: 'Geçersiz kullanıcı adı veya şifre',
        });
      } else {
        setErrors(['Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.']);
        toast({
          variant: 'destructive',
          title: 'Bağlantı Hatası',
          description: 'Giriş yapılırken bir hata oluştu',
        });
      }
    }
  };

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <div className="flex flex-col gap-6">
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-2">
              <form className="p-6 md:p-8" onSubmit={handleSubmit}>
                <FieldGroup>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <h1 className="text-2xl font-bold">Hoş geldiniz</h1>
                    <p className="text-muted-foreground text-balance">
                      Mehir Backoffice hesabınıza giriş yapın
                    </p>
                  </div>

                  {errors.length > 0 && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        <ul className="list-disc pl-4 space-y-1">
                          {errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  <Field>
                    <FieldLabel htmlFor="username">Kullanıcı Adı</FieldLabel>
                    <Input
                      id="username"
                      type="text"
                      autoComplete="username"
                      placeholder="kullanici.adi"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="password">Şifre</FieldLabel>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="Şifrenizi girin"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </Field>

                  <Field>
                    <Button type="submit" disabled={isLoading} className="w-full">
                      {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                    </Button>
                  </Field>
                </FieldGroup>
              </form>

              <div className="bg-muted relative hidden md:block">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="mb-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">Güvenli Admin Paneli</h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        Mehir uygulamanızın yönetim paneline hoş geldiniz. Güvenli bir ortamda çalışın.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
